import { type Repub } from '@cx-modules/constants';

export const featureFlags = {
  DISABLE_HEADLESS_OFFERS_I18N_CACHE: 'disable-headless-offers-i18n-cache',
  DISABLE_ADOBE_ANALYTICS: 'disable-adobe-analytics',
  DISABLE_BEST_BETS_WIDGET: 'disable-best-bets-widget',
  DISABLE_GOOGLE_TAG_MANAGER: 'disable-google-tag-manager',
  DISABLE_GRID_TAB_STATS: 'disable-grid-tab-stats',
  DISABLE_MARKET_GROUPS_API: 'disable-market-groups-api',
  DISABLE_OFFER_WIDGETS: 'disable-offer-widgets',
  DISABLE_SENTRY: 'disable-sentry',
  DISABLE_VWO: 'disable-vwo',
  ENABLE_LIVE_PRICES: 'enable-live-prices',
  RENDER_GTM_POST_HYDRATION: 'render-gtm-post-hydration',
  SUBS__PLUS__ALL_WNBA: 'subs__plus__all_wnba',
  SUBS__PLUS__HERO_PRICING: 'subs__plus__hero__pricing',
  SUBS__PLUS__INTERNAL_SEARCH_TOOLS: 'subs__plus__internal_search_tools',
  SUBS__PLUS__LAST_10_PERSISTENT_SORT: 'subs__plus__last_10_persistent_sort',
  SUBS__PLUS__LINE_REDUCTION: 'subs__plus__line_reduction',
  SUBS__PLUS__NEW_CATEGORY_GROUPS: 'subs__plus__new_category_groups',
  SUBS__PLUS__PADLOCKING: 'subs__plus__padlocking',
  SUBS__PLUS__Q4_PROPS: 'subs__plus__q4_props',
  SUBS__PLUS__SOCCER_BETTING_SPLITS: 'subs__plus__soccer_betting_splits',
  SUBS__PLUS__SOCCER_EPL_ONLY: 'subs__plus__soccer_epl_only',
  SUBS__PLUS__SOCCER_EV_TRENDS: 'subs__plus__soccer_ev_trends',
  SUBS__PLUS__SOCCER_TRENDS: 'subs__plus__soccer_trends',
  SUBS__PLUS__STATS_TABLE: 'subs__plus__stats_table',
  SUBS__PLUS__TEXT_SEARCH: 'subs__plus__text_search',
  SUBS__PLUS__TIPSTERS: 'subs__plus__tipsters',
  SUBS__PLUS__TRENDS__BARCHART: 'subs__plus__trends__barchart',
  SUBS__PLUS__WORLD_CUP26: 'subs__plus__world_cup26',
  RENDER_BEST_BETS_WIDGET_FULL_WIDTH: 'render-best-bets-widget-full-width',
  RENDER_ONBOARDING_AS_MYOC_LOGIN_CTA: 'render-onboarding-as-myoc-login-cta',
  RENDER_UK_HEADER_STYLE: 'render-uk-header-style',
  RENDER_UK_FOOTER_STYLE: 'render-uk-footer-style',
  RENDER_UK_BOTTOM_NAV_STYLE: 'render-uk-bottom-nav-style',
};

/**
 * Request header that opts a single request into the UK header restyle at runtime (NGF-6475),
 * `oc-header-style: uk`. The analogue of the proxy's `oc-platform: astro` canary header
 * (apps/proxy/server.ts): it can be toggled per-request from a browser header-override extension or
 * curl — no env var, no server restart, no Unleash toggle flip. Unlike `oc-platform`, this header does
 * NOT affect proxy routing, so the proxy forwards it through untouched; it is read where the flag is
 * resolved (atlas's build-page-data loader, atlas-astro's evaluateFeatureFlags).
 */
export const UK_HEADER_STYLE_OVERRIDE_HEADER = 'oc-header-style';
const UK_HEADER_STYLE_OVERRIDE_VALUE = 'uk';

/**
 * Whether the incoming request opts into the UK header restyle via the `oc-header-style: uk` header.
 * Framework-agnostic — takes the raw header value so callers pass it from a web `Headers.get()`, a
 * Node `IncomingMessage.headers[...]`, etc. Mirrors the proxy's `isAstroPlatform` helper.
 */
export function isUkHeaderStyleForced(headerValue: string | null | undefined): boolean {
  return headerValue === UK_HEADER_STYLE_OVERRIDE_VALUE;
}

/**
 * Request header that opts a single request into the UK footer restyle at runtime, `oc-footer-style:
 * uk`. The footer twin of {@link UK_HEADER_STYLE_OVERRIDE_HEADER} — a per-request dev/QA override
 * (no env var, no server restart, no Unleash flip) that does NOT affect proxy routing. Like every
 * debug header it is inert unless `oc-debug: true` accompanies it (NGF-6738); resolve it via
 * `@cx-web/utils/debugRequest`'s `resolveDebugSwitch`, never by reading the header directly.
 */
export const UK_FOOTER_STYLE_OVERRIDE_HEADER = 'oc-footer-style';
const UK_FOOTER_STYLE_OVERRIDE_VALUE = 'uk';

/**
 * Whether the incoming request opts into the UK footer restyle via `oc-footer-style: uk`. The footer
 * twin of {@link isUkHeaderStyleForced} — framework-agnostic, takes the already-resolved switch value.
 */
export function isUkFooterStyleForced(headerValue: string | null | undefined): boolean {
  return headerValue === UK_FOOTER_STYLE_OVERRIDE_VALUE;
}

/**
 * Request header that opts a single request into the UK mobile-bottom-nav restyle, `oc-bottom-nav-style:
 * uk`. The bottom-nav twin of {@link UK_HEADER_STYLE_OVERRIDE_HEADER}. Like every debug header it is
 * inert unless `oc-debug: true` accompanies it (NGF-6738); resolve it via `@cx-web/utils/debugRequest`'s
 * `resolveDebugSwitch`, never by reading the header directly.
 */
export const UK_BOTTOM_NAV_STYLE_OVERRIDE_HEADER = 'oc-bottom-nav-style';
const UK_BOTTOM_NAV_STYLE_OVERRIDE_VALUE = 'uk';

/**
 * Whether the incoming request opts into the UK mobile-bottom-nav restyle via `oc-bottom-nav-style: uk`.
 * The bottom-nav twin of {@link isUkHeaderStyleForced} — framework-agnostic, takes the already-resolved
 * switch value.
 */
export function isUkBottomNavStyleForced(headerValue: string | null | undefined): boolean {
  return headerValue === UK_BOTTOM_NAV_STYLE_OVERRIDE_VALUE;
}

/**
 * Request switch that opts a single request into the NGF-6416 UK odds-grid redesign,
 * `oc-odds-grid-style: uk`. The grid twin of {@link UK_HEADER_STYLE_OVERRIDE_HEADER} — a per-request
 * dev/QA override that does NOT affect proxy routing. Like every debug header it is inert unless
 * `oc-debug: true` accompanies it (NGF-6738); resolve it via `@cx-web/utils/debugRequest`'s
 * `resolveDebugSwitch`, never by reading the header directly.
 *
 * Unlike the header/footer switches this has NO Unleash flag behind it: the switch IS the gate, so an
 * unswitched request always renders `@cx-modules/odds-grid/legacy` (the pre-NGF-6416 grid). The flag
 * arrives with the rollout MR.
 */
export const UK_ODDS_GRID_OVERRIDE_HEADER = 'oc-odds-grid-style';
const UK_ODDS_GRID_OVERRIDE_VALUE = 'uk';

/**
 * Whether the incoming request opts into the UK odds grid via `oc-odds-grid-style: uk`. Takes the
 * already-resolved switch value, like {@link isUkFooterStyleForced}.
 */
export function isUkOddsGridForced(switchValue: string | null | undefined): boolean {
  return switchValue === UK_ODDS_GRID_OVERRIDE_VALUE;
}

/**
 * Request switch that opts a single request into the UK betslip, `oc-betslip-style: uk`.
 *
 * Exists so the betslip stops riding on {@link UK_HEADER_STYLE_OVERRIDE_HEADER}. Before this,
 * `shouldUseUkCdnBetslip` took the resolved UK-*header* flag, so previewing the new header also swapped
 * the betslip and there was no way to move one without the other. The call site now ORs this switch into
 * that argument, which leaves today's behaviour unchanged while giving the betslip redesign its own
 * lever. Same `oc-debug` gating and `resolveDebugSwitch` rule as every other switch here.
 */
export const UK_BETSLIP_OVERRIDE_HEADER = 'oc-betslip-style';
const UK_BETSLIP_OVERRIDE_VALUE = 'uk';

/** Whether the incoming request opts into the UK betslip via `oc-betslip-style: uk`. */
export function isUkBetslipForced(switchValue: string | null | undefined): boolean {
  return switchValue === UK_BETSLIP_OVERRIDE_VALUE;
}

/**
 * Derives the per-repub Unleash toggle name for the new desktop header (OCGC-3417), e.g.
 * `desktop-new-header-style-us`. Deliberately NOT a `featureFlags` registry entry — one per repub
 * (22 today) would bloat the shared registry and {@link featureFlags}'s bootstrap seeding
 * (unleash.server.ts), for a flag set that is temporary rollout scaffolding. A name absent from
 * Unleash/GitLab already evaluates `false` via `isEnabled`, so the flag works safely before it exists
 * in GitLab and after it is deleted post-rollout — no registry/bootstrap entry required either way.
 */
export function desktopNewHeaderStyleFlagName(repub: Repub): string {
  return `desktop-new-header-style-${repub.toLowerCase()}`;
}

/**
 * Derives the per-repub Unleash toggle name for the new mobile header, e.g.
 * `mobile-new-header-style-us`. Like {@link desktopNewHeaderStyleFlagName}, this remains outside
 * the shared registry because it is temporary per-repub rollout scaffolding and absent flags
 * safely evaluate false.
 */
export function mobileNewHeaderStyleFlagName(repub: Repub): string {
  return `mobile-new-header-style-${repub.toLowerCase()}`;
}

/**
 * Whether the new desktop header should render for this request: the existing global kill switch
 * (RENDER_UK_HEADER_STYLE) AND the requesting repub's own {@link desktopNewHeaderStyleFlagName} flag.
 * Both must be on — the global flag stays a hard kill switch during the per-repub rollout, so flipping
 * it off hides the new header everywhere regardless of any repub's individual flag state, while the
 * per-repub flag lets each territory roll out independently once the global switch is on.
 *
 * Callers that apply the `oc-header-style: uk` dev override must force BOTH inputs on — i.e. OR the
 * override into each input (`forceUkHeaderStyle || unleash.isEnabled(...)`) — otherwise forcing only the
 * global flag leaves this AND resolving false against a local/dev Unleash instance where the per-repub
 * flag is off or absent.
 */
export function resolveDesktopNewHeaderStyle(
  renderUkHeaderStyle: boolean,
  desktopNewHeaderStyleForRepub: boolean,
): boolean {
  return renderUkHeaderStyle && desktopNewHeaderStyleForRepub;
}

/**
 * Whether the new mobile header should render for this request. It shares the existing global
 * UK-header kill switch with the desktop rollout, but uses an independent per-repub mobile flag;
 * desktop and mobile can therefore roll out separately.
 */
export function resolveMobileNewHeaderStyle(
  renderUkHeaderStyle: boolean,
  mobileNewHeaderStyleForRepub: boolean,
): boolean {
  return renderUkHeaderStyle && mobileNewHeaderStyleForRepub;
}

/**
 * Derives the per-repub Unleash toggle name for the new footer (footer migration), e.g.
 * `render-uk-footer-style-us`. The footer twin of {@link desktopNewHeaderStyleFlagName}. Deliberately
 * NOT a `featureFlags` registry entry — one per repub is temporary rollout scaffolding; a name absent
 * from Unleash already evaluates `false` via `isEnabled`, so it works safely before it exists in GitLab
 * and after it is deleted post-rollout. (Uses the consistent `render-uk-footer-style-` stem rather than
 * the header's divergent `desktop-new-header-style-` one.)
 */
export function ukFooterStyleFlagName(repub: Repub): string {
  return `render-uk-footer-style-${repub.toLowerCase()}`;
}

/**
 * Whether the new footer should render for this request: the global kill switch (RENDER_UK_FOOTER_STYLE)
 * AND the requesting repub's own {@link ukFooterStyleFlagName} flag. The footer twin of
 * {@link resolveDesktopNewHeaderStyle} — both must be on, so the global flag stays a hard kill switch
 * during the per-repub rollout while each territory rolls out independently. Callers applying the
 * `oc-footer-style: uk` override must force BOTH inputs on — i.e. OR the override into each input
 * (`forceUkFooterStyle || unleash.isEnabled(...)`).
 */
export function resolveUkFooterStyle(renderUkFooterStyle: boolean, ukFooterStyleForRepub: boolean): boolean {
  return renderUkFooterStyle && ukFooterStyleForRepub;
}

/**
 * Derives the per-repub Unleash toggle name for the new mobile bottom nav, e.g.
 * `render-uk-bottom-nav-style-us`. The bottom-nav twin of {@link ukFooterStyleFlagName}; same reasoning
 * for staying out of the `featureFlags` registry.
 */
export function ukBottomNavStyleFlagName(repub: Repub): string {
  return `render-uk-bottom-nav-style-${repub.toLowerCase()}`;
}

/**
 * Whether the new mobile bottom nav should render: the global kill switch (RENDER_UK_BOTTOM_NAV_STYLE)
 * AND the requesting repub's own {@link ukBottomNavStyleFlagName} flag. Callers applying the
 * `oc-bottom-nav-style: uk` override must force BOTH inputs on, as the header and footer twins do.
 */
export function resolveUkBottomNavStyle(renderUkBottomNavStyle: boolean, ukBottomNavStyleForRepub: boolean): boolean {
  return renderUkBottomNavStyle && ukBottomNavStyleForRepub;
}
